// Generic room logic shared by the server host and the in-browser local host.
// Everything here is synchronous and deterministic given (room, now, rng state).

import type { ContentAccess } from "./content";
import { activePlayers, isError, type BotPlan, type Engine, type EngineCtx } from "./engine";
import { bluffEngine } from "./games/bluff";
import { guessEngine } from "./games/guess";
import { matchEngine } from "./games/match";
import { spyEngine } from "./games/spy";
import { trufEngine } from "./games/truf";
import { AVATARS, PLAYER_COLORS } from "./palette";
import { createRng, createSecureRng, type Rng } from "./rng";
import { cleanText, normalizeAnswer } from "./text";
import type {
  ActResult, ErrorCode, GameId, Lang, PlayerPublic, PlayerRec, Role, RoomAction, RoomCore, RoomView,
} from "./types";

export const ENGINES: Record<GameId, Engine> = {
  bluff: bluffEngine,
  spy: spyEngine,
  match: matchEngine,
  truf: trufEngine,
  guess: guessEngine,
};

export const MAX_NAME = 18;
const MAX_AUDIENCE = 60;

export interface HostDeps {
  now: number;
  content: ContentAccess;
}

const BOT_NAMES: Record<Lang, string[]> = {
  ka: ["ბოტი ზურა", "ბოტი ნინო", "ბოტი გელა", "ბოტი თამრიკო", "ბოტი ლევანი", "ბოტი ქეთი", "ბოტი გოჩა", "ბოტი სალომე"],
  en: ["Bot Zura", "Bot Nino", "Bot Gela", "Bot Tamriko", "Bot Levan", "Bot Keti", "Bot Gocha", "Bot Salome"],
  ru: ["Бот Зура", "Бот Нино", "Бот Гела", "Бот Тамрико", "Бот Леван", "Бот Кети", "Бот Гоча", "Бот Саломе"],
};

export function withRng<T>(room: RoomCore, fn: (rng: Rng) => T): T {
  if (room.secure) return fn(createSecureRng());
  const rng = createRng(room.rngState);
  const out = fn(rng);
  room.rngState = rng.state();
  return out;
}

function ctxFor(room: RoomCore, deps: HostDeps, rng: Rng): EngineCtx {
  return { now: deps.now, rng, content: deps.content };
}

export function createRoomCore(p: {
  code: string;
  game: GameId;
  lang: Lang;
  local: boolean;
  seed: number;
  now: number;
  settings?: unknown;
}): RoomCore {
  const engine = ENGINES[p.game];
  const defaults = engine.defaults(p.lang);
  return {
    code: p.code,
    game: p.game,
    lang: p.lang,
    local: p.local,
    createdAt: p.now,
    updatedAt: p.now,
    version: 1,
    rngState: p.seed >>> 0,
    hostId: "",
    players: [],
    settings: p.settings ? engine.sanitize(p.settings, defaults) : defaults,
    phase: "lobby",
    state: null,
    paused: null,
    round: 0,
    usedContent: [],
    kicked: [],
  };
}

function uniqueName(room: RoomCore, wanted: string, exceptId?: string): string {
  const base = cleanText(wanted, MAX_NAME) || "?";
  const taken = new Set(room.players.filter((p) => p.id !== exceptId).map((p) => normalizeAnswer(p.name)));
  if (!taken.has(normalizeAnswer(base))) return base;
  for (let i = 2; i < 100; i++) {
    const candidate = `${base.slice(0, MAX_NAME - 3)} ${i}`;
    if (!taken.has(normalizeAnswer(candidate))) return candidate;
  }
  return `${base.slice(0, MAX_NAME - 4)} ${Date.now() % 1000}`;
}

function freeColor(room: RoomCore, rngInt: (n: number) => number): number {
  const used = new Set(room.players.map((p) => p.color));
  const free = PLAYER_COLORS.map((_, i) => i).filter((i) => !used.has(i));
  return free.length ? free[0] : rngInt(PLAYER_COLORS.length);
}

export function addPlayer(
  room: RoomCore,
  p: { name: string; role?: Role; isBot?: boolean; userId?: number | null; avatar?: number },
  deps: HostDeps,
): PlayerRec | ErrorCode {
  const engine = ENGINES[room.game];
  let role: Role = p.role ?? "player";
  const players = activePlayers(room).length;
  if (role === "player" && players >= engine.maxPlayers) {
    if (p.isBot) return "room_full";
    role = "audience";
  }
  if (role === "audience" && room.players.filter((x) => x.role === "audience").length >= MAX_AUDIENCE) return "room_full";
  const rec = withRng(room, (rng) => {
    const used = new Set(room.players.map((x) => x.avatar));
    const freeAvatars = AVATARS.map((_, i) => i).filter((i) => !used.has(i));
    // an avatar belongs to one player per room: a taken one is swapped for a free one
    const wanted = typeof p.avatar === "number" && p.avatar >= 0 && p.avatar < AVATARS.length ? p.avatar : null;
    const avatar =
      wanted !== null && (!used.has(wanted) || !freeAvatars.length)
        ? wanted
        : freeAvatars.length ? rng.pick(freeAvatars) : rng.int(AVATARS.length);
    const player: PlayerRec = {
      id: rng.id(8),
      name: uniqueName(room, p.name),
      color: freeColor(room, rng.int),
      avatar,
      role,
      isBot: !!p.isBot,
      connected: true,
      joinedAt: deps.now,
      lastSeen: deps.now,
      userId: p.userId ?? null,
    };
    return player;
  });
  room.players.push(rec);
  if (!room.hostId && !rec.isBot) room.hostId = rec.id;
  if (room.phase === "game" && room.state && rec.role === "player") {
    withRng(room, (rng) => {
      const e = ENGINES[room.game];
      if (e.onJoin) room.state = e.onJoin(room, room.state, rec.id, ctxFor(room, deps, rng));
    });
  }
  touch(room, deps.now);
  return rec;
}

function touch(room: RoomCore, now: number) {
  room.version += 1;
  room.updatedAt = now;
}

function removePlayer(room: RoomCore, id: string, deps: HostDeps) {
  const idx = room.players.findIndex((p) => p.id === id);
  if (idx < 0) return;
  room.players.splice(idx, 1);
  if (room.phase === "game" && room.state) {
    withRng(room, (rng) => {
      const e = ENGINES[room.game];
      if (e.onLeave) room.state = e.onLeave(room, room.state, id, ctxFor(room, deps, rng));
    });
  }
  if (room.hostId === id) {
    const next = room.players.find((p) => !p.isBot && p.connected) ?? room.players.find((p) => !p.isBot);
    room.hostId = next?.id ?? "";
  }
}

export function applyAction(room: RoomCore, actorId: string, action: RoomAction, deps: HostDeps): ActResult {
  const actor = room.players.find((p) => p.id === actorId);
  if (!actor) return { ok: false, error: "not_found" };
  if (!action || typeof action !== "object") return { ok: false, error: "bad_input" };
  const engine = ENGINES[room.game];
  const isHost = room.hostId === actorId || room.local;
  const fail = (error: ErrorCode): ActResult => ({ ok: false, error });
  actor.lastSeen = deps.now;

  switch (action.t) {
    case "settings": {
      if (!isHost) return fail("not_host");
      if (room.phase !== "lobby") return fail("bad_phase");
      room.settings = engine.sanitize(action.settings, room.settings);
      break;
    }
    case "start":
    case "again": {
      if (!isHost) return fail("not_host");
      if (action.t === "start" && room.phase !== "lobby") return fail("bad_phase");
      if (action.t === "again" && !(room.phase === "game" && room.state && engine.isOver(room.state))) return fail("bad_phase");
      if (activePlayers(room).length < engine.minPlayers) return fail("too_few_players");
      const result = withRng(room, (rng) => engine.start(room, ctxFor(room, deps, rng)));
      if (isError(result)) return fail(result);
      room.state = result;
      room.phase = "game";
      room.paused = null;
      room.round += 1;
      break;
    }
    case "lobby": {
      if (!isHost) return fail("not_host");
      room.phase = "lobby";
      room.state = null;
      room.paused = null;
      break;
    }
    case "kick": {
      if (!isHost) return fail("not_host");
      if (action.id === actorId) return fail("bad_input");
      const target = room.players.find((p) => p.id === action.id);
      if (!target) return fail("not_found");
      room.kicked.push(target.id);
      removePlayer(room, target.id, deps);
      break;
    }
    case "addBot": {
      if (!isHost) return fail("not_host");
      if (!engine.supportsBots) return fail("bad_input");
      if (room.phase !== "lobby") return fail("bad_phase");
      const names = BOT_NAMES[room.lang];
      const taken = new Set(room.players.map((p) => p.name));
      const name = names.find((n) => !taken.has(n)) ?? `${names[0]} ${room.players.length}`;
      const bot = addPlayer(room, { name, isBot: true }, deps);
      if (isError(bot)) return fail(bot);
      return { ok: true }; // addPlayer already touched
    }
    case "removeBot": {
      if (!isHost) return fail("not_host");
      const target = room.players.find((p) => p.id === action.id && p.isBot);
      if (!target) return fail("not_found");
      removePlayer(room, target.id, deps);
      break;
    }
    case "rename": {
      const name = cleanText(action.name, MAX_NAME);
      if (!name) return fail("bad_input");
      actor.name = uniqueName(room, name, actor.id);
      break;
    }
    case "avatar": {
      const a = Number(action.avatar);
      if (!Number.isInteger(a) || a < 0 || a >= AVATARS.length) return fail("bad_input");
      if (room.players.some((x) => x.id !== actor.id && x.avatar === a)) return fail("avatar_taken");
      actor.avatar = a;
      if (typeof action.color === "number" && action.color >= 0 && action.color < PLAYER_COLORS.length) {
        actor.color = action.color;
      }
      break;
    }
    case "role": {
      if (room.phase !== "lobby") return fail("bad_phase");
      if (action.role !== "player" && action.role !== "audience") return fail("bad_input");
      if (action.role === "player" && actor.role !== "player" && activePlayers(room).length >= engine.maxPlayers) {
        return fail("room_full");
      }
      actor.role = action.role;
      break;
    }
    case "pause": {
      if (!isHost) return fail("not_host");
      if (room.phase !== "game" || room.paused) return fail("bad_phase");
      room.paused = { at: deps.now };
      if (engine.onPause && room.state) {
        withRng(room, (rng) => {
          room.state = engine.onPause!(room, room.state, ctxFor(room, deps, rng));
        });
      }
      break;
    }
    case "resume": {
      if (!isHost) return fail("not_host");
      if (!room.paused) return fail("bad_phase");
      const delta = deps.now - room.paused.at;
      room.paused = null;
      if (engine.onResume && room.state) {
        withRng(room, (rng) => {
          room.state = engine.onResume!(room, room.state, delta, ctxFor(room, deps, rng));
        });
      } else if (room.state && typeof room.state.deadline === "number") room.state.deadline += delta;
      break;
    }
    case "host": {
      if (!isHost) return fail("not_host");
      const target = room.players.find((p) => p.id === action.id && !p.isBot);
      if (!target) return fail("not_found");
      room.hostId = target.id;
      break;
    }
    case "leave": {
      removePlayer(room, actorId, deps);
      break;
    }
    case "g": {
      if (room.phase !== "game" || !room.state) return fail("bad_phase");
      if (room.paused) return fail("paused");
      const res = withRng(room, (rng) => engine.act(room, room.state, actorId, action.a, ctxFor(room, deps, rng)));
      if (isError(res)) return fail(res);
      room.state = res;
      break;
    }
    default:
      return fail("bad_input");
  }
  touch(room, deps.now);
  return { ok: true };
}

/** A server-internal message for the running game (see Engine.system). Returns true when applied. */
export function applySystem(room: RoomCore, msg: unknown, deps: HostDeps): boolean {
  const engine = ENGINES[room.game];
  if (room.phase !== "game" || !room.state || !engine.system) return false;
  // engines mutate their state in place, so every applied message counts as a change
  withRng(room, (rng) => {
    room.state = engine.system!(room, room.state, msg, ctxFor(room, deps, rng));
  });
  touch(room, deps.now);
  return true;
}

/** Run every due engine deadline. Returns true when the room changed. */
export function runTimers(room: RoomCore, deps: HostDeps): boolean {
  if (room.phase !== "game" || !room.state || room.paused) return false;
  const engine = ENGINES[room.game];
  let changed = false;
  for (let guard = 0; guard < 20; guard++) {
    const dl = room.state.deadline;
    if (typeof dl !== "number" || dl > deps.now) break;
    withRng(room, (rng) => {
      room.state = engine.tick(room, room.state, ctxFor(room, deps, rng));
    });
    changed = true;
  }
  if (changed) touch(room, deps.now);
  return changed;
}

export function nextWake(room: RoomCore): number | null {
  if (room.phase !== "game" || !room.state || room.paused) return null;
  const dl = room.state.deadline;
  return typeof dl === "number" ? dl : null;
}

export function botPlans(room: RoomCore, deps: HostDeps): { botId: string; plan: BotPlan }[] {
  if (room.phase !== "game" || !room.state || room.paused) return [];
  const engine = ENGINES[room.game];
  if (!engine.bot) return [];
  const out: { botId: string; plan: BotPlan }[] = [];
  for (const p of room.players) {
    if (!p.isBot || p.role !== "player") continue;
    const plan = withRng(room, (rng) => engine.bot!(room, room.state, p.id, ctxFor(room, deps, rng)));
    if (plan) out.push({ botId: p.id, plan });
  }
  return out;
}

export function publicPlayers(room: RoomCore): PlayerPublic[] {
  return room.players.map((p) => ({
    id: p.id,
    name: p.name,
    color: p.color,
    avatar: p.avatar,
    role: p.role,
    isBot: p.isBot,
    isHost: p.id === room.hostId,
    connected: p.connected,
  }));
}

export function buildView(room: RoomCore, viewerId: string | null, now: number): RoomView {
  const me = viewerId ? room.players.find((p) => p.id === viewerId) : undefined;
  const engine = ENGINES[room.game];
  return {
    code: room.code,
    game: room.game,
    lang: room.lang,
    local: room.local,
    version: room.version,
    serverNow: now,
    hostId: room.hostId,
    me: me ? { id: me.id, role: me.role, isHost: me.id === room.hostId || room.local } : null,
    players: publicPlayers(room),
    settings: room.settings,
    phase: room.phase,
    paused: !!room.paused,
    round: room.round,
    view: room.phase === "game" && room.state ? engine.view(room, room.state, viewerId, now) : null,
  };
}

export function gameOver(room: RoomCore): boolean {
  return room.phase === "game" && !!room.state && ENGINES[room.game].isOver(room.state);
}
